import apiClient from '../config/axiosConfig.js';

/**
 * Derivation links — "this file was exported from that one".
 *
 * Thin wrappers over the shared apiClient (which injects auth + x-workspace-id).
 * Callers that render their own inline error pass `{ skipGlobalErrorToast: true }`
 * so a failure isn't reported twice.
 *
 * @typedef {Object} DerivationRelative
 * @property {string} fileId
 * @property {string} name
 * @property {string|null} stage
 * @property {number} versionNumber
 * @property {string} mimetype
 * @property {number|null} [baselineVersion]  Source version this was made from.
 * @property {number} [lag]                   Versions the source has moved on by.
 * @property {string} [origin]
 * @property {boolean} [missing]              Relative no longer exists.
 *
 * @typedef {Object} DerivationView
 * @property {string} fileId
 * @property {string|null} stage
 * @property {'none'|'unknown'|'ok'|'stale'} state
 * @property {number} staleBy
 * @property {number} staleExports
 * @property {string|null} primarySourceId
 * @property {string|null} primaryExportId
 * @property {DerivationRelative[]} sources
 * @property {DerivationRelative[]} exports
 */

/**
 * Everything about one file's links, resolved server-side in a single fan-out
 * read (names, versions and lag included) so the UI never walks the graph.
 * @returns {Promise<DerivationView>}
 */
export async function getFileDerivation(fileId, options = {}) {
    const res = await apiClient.get('/api/files/derivation', { params: { id: fileId }, ...options });
    return res.data.derivation;
}

/**
 * Record that `derivedFileId` was produced from `sourceFileId`.
 *
 * `establishBaseline` claims the derived file is current with the source's
 * version RIGHT NOW. Only pass it when that is actually known — pairing two
 * files that already existed cannot know it, and a wrong baseline reads as a
 * false all-clear rather than an honest "not established".
 */
export async function linkDerivation(sourceFileId, derivedFileId, { establishBaseline = false } = {}, options = {}) {
    const res = await apiClient.post(
        '/api/files/derivation/link',
        { sourceFileId, derivedFileId, establishBaseline },
        options,
    );
    return res.data;
}

export async function unlinkDerivation(sourceFileId, derivedFileId, options = {}) {
    const res = await apiClient.delete('/api/files/derivation/link', {
        params: { sourceFileId, derivedFileId },
        ...options,
    });
    return res.data;
}

/**
 * "These are in sync now." Stamps the source's current version as the baseline,
 * which is what turns "baseline not established" into a usable staleness signal.
 * Omit `sourceFileId` to stamp every source of the derived file.
 */
export async function establishBaseline(derivedFileId, sourceFileId, options = {}) {
    const res = await apiClient.post(
        '/api/files/derivation/baseline',
        { derivedFileId, ...(sourceFileId ? { sourceFileId } : {}) },
        options,
    );
    return res.data;
}

/** Pin which relative fills the "primary" slot (and, from P6, the thumbnail). */
export async function setDerivationPrimary(fileId, pins, options = {}) {
    const res = await apiClient.post('/api/files/derivation/primary', { fileId, ...pins }, options);
    return res.data;
}

/** Classify a file's place in the pipeline: 'raw' | 'source' | 'export' | null. */
export async function setFileStage(fileId, stage, options = {}) {
    const res = await apiClient.post('/api/files/stage', { fileId, stage }, options);
    return res.data;
}

/**
 * Pair up a project's sources and exports by name and path.
 *
 * Confident matches are written immediately; anything ambiguous lands in the
 * review queue. `dryRun` reports what would happen and writes nothing, which is
 * what the first run over an existing library should do.
 *
 * @returns {Promise<{linked: number, suggested: number, orphanSources: number,
 *                    truncated: boolean, applied: Array<object>}>}
 */
export async function rematchProject(projectId, { dryRun = false } = {}, options = {}) {
    const res = await apiClient.post('/api/projects/rematch', { projectId, dryRun }, options);
    return res.data;
}

/**
 * Declare every linked pair in a project current, as of now.
 *
 * The matcher can't know which source revision produced which export, so it
 * leaves the baseline unset and the staleness warnings stay silent. This is the
 * one bulk assertion that switches them on — a person stating that today's state
 * is the reference point.
 *
 * @returns {Promise<{files: number, edges: number, truncated: boolean}>}
 */
export async function baselineProjectLinks(projectId, options = {}) {
    const res = await apiClient.post('/api/projects/link-baselines', { projectId }, options);
    return res.data;
}

/**
 * The pending review queue for a project, strongest proposal first.
 * @returns {Promise<Array<object>>}
 */
export async function listLinkSuggestions(projectId, options = {}) {
    const res = await apiClient.get('/api/projects/link-suggestions', {
        params: { id: projectId },
        ...options,
    });
    return res.data.suggestions ?? [];
}

/**
 * Accept or reject one proposal.
 *
 * Accepting creates a real link and drops the suggestion. Rejecting keeps a
 * tombstone so the deterministic matcher never proposes the pair again.
 */
export async function resolveLinkSuggestion(suggestionId, action, options = {}) {
    const res = await apiClient.post('/api/projects/link-suggestions/resolve', { suggestionId, action }, options);
    return res.data;
}

/**
 * Find files to link to, by name.
 *
 * Goes through Algolia rather than listing the workspace and filtering in the
 * browser: `getFiles` returns EVERY file in the workspace, which is a multi-MB
 * response at 50k files — far too much to pay for a picker someone opens to type
 * three letters.
 *
 * @returns {Promise<Array<{id: string, name: string, mimetype: string, stage: string|null}>>}
 */
export async function searchFilesByName(query, { hitsPerPage = 8 } = {}, options = {}) {
    const res = await apiClient.post(
        '/api/search/algolia',
        { query: String(query || ''), page: 0, hitsPerPage, facets: [] },
        options,
    );
    return (res.data?.hits ?? []).map((hit) => ({
        id: hit.id ?? hit.objectID,
        name: hit.metadata?.name ?? '',
        mimetype: hit.metadata?.mimetype ?? '',
        stage: hit.stage ?? null,
    }));
}
